import React, { FunctionComponent, useEffect, useState } from "react";
import { IAlbum } from "../../../hooks/albums";
import { strings } from "../../../localization/LocalizedStrings";
import { AlertDialog } from "../../AlertDialog";
import {
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Tooltip,
  useTheme
} from "@mui/material";
import styles from "./styles.module.scss";
import { StatusCodesHelper } from "../../../models/StatusCodesHelper";
import { CollectionCard } from "../../collections/CollectionCard";
import { CollectionsDialog } from "../../collections/CollectionsDialog";
import { useAssignAlbumToCollection } from "../../../hooks/albums/useAssignAlbumToCollection";
import { useUnassignAlbumFromCollection } from "../../../hooks/albums/useUnassignAlbumFromCollection";
import { toast } from "react-toastify";
import { ICollection } from "../../../hooks/collections";
import { useIsMobile } from "../../../hooks/isMobile";
import { StringFormat } from "../../../models/StringFormat";
import { useGetShareLink } from "../../../hooks/share";
import { useCurrentPublicCollection } from "../../../hooks/collections/useCollectionManager";
import { usePublicUserProfile } from "../../../hooks/session";
import { ToolbarButton } from "../../toolbar/ToolbarButton";
import { useContextMenu } from "../../../hooks/contextMenu";
import { useShareLink } from "../../../hooks/share/useShareLink";
import { useOpenWindow } from "../../../hooks/openWindow";
import { Icon, IconType } from "../../../icons";

export const AlbumCollectionsListSection: FunctionComponent<IAlbumCollectionsListSectionProps> = ({
  album
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const [collections, setCollections] = useState<ICollection[] | undefined>(album.collections);
  const [selectedCollection, setSelectedCollection] = useState<ICollection | undefined>();
  const [, setCurrentPublicCollection] = useCurrentPublicCollection.useState();
  const [, setCurrentPublicUserProfile] = usePublicUserProfile.useState();
  const { assignAlbumToCollection } = useAssignAlbumToCollection();
  const { unassignAlbumFromCollection } = useUnassignAlbumFromCollection();
  const { getShareLink } = useGetShareLink();
  const [isCollectionsDialogOpen, setIsCollectionsDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUnassignAlertOpen, setIsUnassignAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const { menuProps, handleContextMenu, handleMenuClose } = useContextMenu();
  const [shareLinkComponents, fetchShareLink, ,] = useShareLink(album.name);
  const { openInNewTab } = useOpenWindow();
  const showAlbum = async (collectionId: string) => {
    setIsLoading(true);
    const { status, body } = await getShareLink({
      albumId: album.uuid,
      collectionId: collectionId
    });
    if (StatusCodesHelper.isSuccessful(status)) {
      setCurrentPublicUserProfile(undefined);
      setCurrentPublicCollection(undefined);
      openInNewTab(body.shareLink);
    }
    setIsLoading(false);
  };
  useEffect(() => {
    setIsLoading(album.collections === undefined);
    setCollections(album.collections);
  }, [album.collections]);
  const onCollectionSelected = (
    collection: ICollection,
    event: React.TouchEvent | React.MouseEvent
  ) => {
    if (isLoading) return;
    setSelectedCollection(collection);
    handleContextMenu(event);
  };
  const onAssignToCollection = async (collection: ICollection) => {
    setIsLoading(true);
    const albumId = album.uuid;
    const { status, body } = await assignAlbumToCollection({
      uuid: album.uuid,
      collectionId: collection.uuid
    });
    setIsLoading(false);
    if (StatusCodesHelper.isSuccessful(status)) {
      toast.success(strings.album_update_success);
      if (album.uuid == albumId) {
        setCollections(body.collections);
      }
    } else {
      setAlertMessage(body.message);
      setIsAlertOpen(true);
    }
  };
  const onUnassignCollection = async () => {
    if (!selectedCollection) return;
    setIsLoading(true);
    const albumId = album.uuid;
    const { status, body } = await unassignAlbumFromCollection({
      uuid: album.uuid,
      collectionId: selectedCollection.uuid
    });
    setIsLoading(false);
    if (StatusCodesHelper.isSuccessful(status)) {
      toast.success(strings.album_update_success);
      if (albumId == album.uuid) {
        setCollections(body.collections);
      }
    } else {
      setAlertMessage(body.message);
      setIsAlertOpen(true);
    }
  };
  return (
    <div className={styles.itemDiv}>
      <Stack className={styles.header} direction={"row"} spacing={2} sx={{ width: "100%" }}>
        <ListItemText
          disableTypography
          className={styles.title}
          secondary={strings.collections_title}
          sx={{ flexGrow: 0 }}
        />
        <ToolbarButton
          tooltip={strings.collection_associate}
          iconType={IconType.LibraryAdd}
          onClick={() => setIsCollectionsDialogOpen(true)}
          loading={isLoading}
        />
      </Stack>
      <Grid container spacing={2} sx={{ minHeight: 136 }}>
        {collections && collections.length > 0 ? (
          collections.map(collection => (
            <Grid key={collection.uuid} item xs={isMobile ? 12 : 6}>
              <CollectionCard
                className={styles.collectionCard}
                key={collection.uuid}
                disabled={isLoading}
                collection={collection}
                onClick={event => onCollectionSelected(collection, event)}
                onContextMenu={event => onCollectionSelected(collection, event)}
              >
                <Stack className={styles.hoverButtonsStack} direction={"row"} spacing={1}>
                  <Tooltip title={strings.options} arrow disableInteractive>
                    <span>
                      <IconButton
                        className={styles.hoverButton}
                        style={{ backgroundColor: theme.palette.colors?.counterBackground }}
                        disabled={isLoading}
                        onClick={event => {
                          event.stopPropagation();
                          setSelectedCollection(collection);
                          handleContextMenu(event);
                        }}
                      >
                        <Icon type={IconType.MoreVertical} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </CollectionCard>
            </Grid>
          ))
        ) : isLoading ? (
          Array.from({ length: isMobile ? 1 : 2 }, (value, index) => (
            <Grid key={index} item xs={isMobile ? 12 : 6} className={styles.skeletonCollection}>
              <Skeleton variant={"rectangular"} className={styles.skeleton} />
            </Grid>
          ))
        ) : (
          <p className={styles.noCollectionText} style={{ color: theme.palette.secondary.main }}>
            {strings.album_no_collections}
          </p>
        )}
      </Grid>
      <Menu {...menuProps}>
        {selectedCollection?.visibility == "public" && (
          <MenuItem
            onClick={async () => {
              handleMenuClose();
              await showAlbum(selectedCollection?.uuid);
            }}
            dense
          >
            <ListItemIcon>
              <Icon type={IconType.Public} />
            </ListItemIcon>
            {strings.album_view_on_exhibition}
          </MenuItem>
        )}
        {selectedCollection?.visibility == "public" && (
          <MenuItem
            onClick={async () => {
              handleMenuClose();
              await fetchShareLink(selectedCollection?.uuid, album.uuid);
            }}
            dense
          >
            <ListItemIcon>
              <Icon type={IconType.Share} />
            </ListItemIcon>
            {strings.share_album}
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            handleMenuClose();
            setSelectedCollection(selectedCollection);
            setIsUnassignAlertOpen(true);
          }}
          style={{ color: theme.palette.colors?.remove }}
          dense
        >
          <ListItemIcon>
            <Icon type={IconType.LayersClear} style={{ color: theme.palette.colors?.remove }} />
          </ListItemIcon>
          {strings.collection_disassociate}
        </MenuItem>
      </Menu>
      <CollectionsDialog
        open={isCollectionsDialogOpen}
        setOpen={setIsCollectionsDialogOpen}
        isLayout={false}
        canModifyCollections={false}
        displayStandardCollections={false}
        onCollectionSelected={onAssignToCollection}
        selectedCollections={collections?.map(collection => collection.uuid) ?? []}
        forceLoading={false}
        canNegate={false}
      />
      {shareLinkComponents()}
      <AlertDialog
        message={StringFormat(
          strings.collection_disassociate_confirmation,
          selectedCollection ? selectedCollection.name : ""
        )}
        open={isUnassignAlertOpen}
        setOpen={setIsUnassignAlertOpen}
        isConfirm
        onConfirm={onUnassignCollection}
      />
      <AlertDialog message={alertMessage} open={isAlertOpen} setOpen={setIsAlertOpen} />
    </div>
  );
};

export interface IAlbumCollectionsListSectionProps {
  album: IAlbum;
}
